import type {
  DataResponse,
  PaginatedResponse,
  Team,
  TeamDetail,
  TeamHub,
  TeamHubAppSummary,
  TeamHubChatSummary,
  TeamHubQuickActionSummary,
  TeamHubTaskTemplateSummary,
  TeamMember,
} from "@/types";
import { api } from "../api";

export const teamService = {
  list: () => api.get<DataResponse<Team[]>>("/teams"),
  get: (teamId: string) => api.get<DataResponse<TeamDetail>>(`/teams/${teamId}`),
  getHub: (teamId: string) => api.get<DataResponse<TeamHub>>(`/teams/${teamId}/hub`),
  listConversations: (teamId: string, params?: { page?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams({
      page: String(params?.page ?? 1),
      page_size: String(params?.pageSize ?? 25),
    });
    return api.get<PaginatedResponse<TeamHubChatSummary>>(`/teams/${teamId}/conversations?${searchParams}`);
  },
  create: (data: { name: string; description?: string }) => api.post<DataResponse<TeamDetail>>("/teams", data),
  update: (teamId: string, data: { name?: string; description?: string }) =>
    api.put<DataResponse<TeamDetail>>(`/teams/${teamId}`, data),
  delete: (teamId: string) => api.delete(`/teams/${teamId}`),
  getMembers: (teamId: string) => api.get<DataResponse<TeamMember[]>>(`/teams/${teamId}/members`),
  addMember: (teamId: string, email: string, role: string) =>
    api.post<DataResponse<TeamMember>>(`/teams/${teamId}/members`, { email, role }),
  updateMemberRole: (teamId: string, userId: string, role: string) =>
    api.put<DataResponse<TeamMember>>(`/teams/${teamId}/members/${userId}`, { role }),
  removeMember: (teamId: string, userId: string) => api.delete(`/teams/${teamId}/members/${userId}`),

  // Team Apps
  assignApp: (teamId: string, data: { appUuid: string; displayOrder?: number }) =>
    api.post<DataResponse<TeamHubAppSummary>>(`/teams/${teamId}/apps`, {
      app_uuid: data.appUuid,
      display_order: data.displayOrder ?? 0,
    }),
  updateApp: (teamId: string, appUuid: string, data: { displayOrder?: number }) =>
    api.put<DataResponse<TeamHubAppSummary>>(`/teams/${teamId}/apps/${appUuid}`, {
      display_order: data.displayOrder,
    }),
  removeApp: (teamId: string, appUuid: string) => api.delete(`/teams/${teamId}/apps/${appUuid}`),

  // Quick Actions
  createQuickAction: (
    teamId: string,
    data: {
      label: string;
      description?: string;
      icon?: string;
      color?: string;
      gradient?: string;
      actionType: string;
      agentUuid?: string;
      appUuid?: string;
      flowUuid?: string;
      targetRoute?: string;
      hintText?: string;
      autoSubmit?: boolean;
      displayOrder?: number;
      isEnabled?: boolean;
    },
  ) =>
    api.post<DataResponse<TeamHubQuickActionSummary>>(`/teams/${teamId}/quick-actions`, {
      label: data.label,
      description: data.description,
      icon: data.icon,
      color: data.color,
      gradient: data.gradient,
      action_type: data.actionType,
      agent_uuid: data.agentUuid,
      app_uuid: data.appUuid,
      flow_uuid: data.flowUuid,
      target_route: data.targetRoute,
      hint_text: data.hintText,
      auto_submit: data.autoSubmit ?? false,
      display_order: data.displayOrder ?? 0,
      is_enabled: data.isEnabled ?? true,
    }),
  updateQuickAction: (
    teamId: string,
    actionUuid: string,
    data: {
      label?: string;
      description?: string;
      icon?: string;
      color?: string;
      gradient?: string;
      actionType?: string;
      agentUuid?: string;
      appUuid?: string;
      flowUuid?: string;
      targetRoute?: string;
      hintText?: string;
      autoSubmit?: boolean;
      displayOrder?: number;
      isEnabled?: boolean;
    },
  ) =>
    api.put<DataResponse<TeamHubQuickActionSummary>>(`/teams/${teamId}/quick-actions/${actionUuid}`, {
      label: data.label,
      description: data.description,
      icon: data.icon,
      color: data.color,
      gradient: data.gradient,
      action_type: data.actionType,
      agent_uuid: data.agentUuid,
      app_uuid: data.appUuid,
      flow_uuid: data.flowUuid,
      target_route: data.targetRoute,
      hint_text: data.hintText,
      auto_submit: data.autoSubmit,
      display_order: data.displayOrder,
      is_enabled: data.isEnabled,
    }),
  deleteQuickAction: (teamId: string, actionUuid: string) => api.delete(`/teams/${teamId}/quick-actions/${actionUuid}`),

  // Task Templates
  createTaskTemplate: (
    teamId: string,
    data: {
      name: string;
      description?: string;
      icon?: string;
      color?: string;
      agentUuid?: string;
      promptTemplate?: string;
      inputPlaceholder?: string;
      config?: Record<string, unknown>;
      displayOrder?: number;
      isEnabled?: boolean;
    },
  ) =>
    api.post<DataResponse<TeamHubTaskTemplateSummary>>(`/teams/${teamId}/task-templates`, {
      name: data.name,
      description: data.description,
      icon: data.icon,
      color: data.color,
      agent_uuid: data.agentUuid,
      prompt_template: data.promptTemplate,
      input_placeholder: data.inputPlaceholder,
      config: data.config ?? {},
      display_order: data.displayOrder ?? 0,
      is_enabled: data.isEnabled ?? true,
    }),
  updateTaskTemplate: (
    teamId: string,
    templateUuid: string,
    data: {
      name?: string;
      description?: string;
      icon?: string;
      color?: string;
      agentUuid?: string;
      promptTemplate?: string;
      inputPlaceholder?: string;
      config?: Record<string, unknown>;
      displayOrder?: number;
      isEnabled?: boolean;
    },
  ) =>
    api.put<DataResponse<TeamHubTaskTemplateSummary>>(`/teams/${teamId}/task-templates/${templateUuid}`, {
      name: data.name,
      description: data.description,
      icon: data.icon,
      color: data.color,
      agent_uuid: data.agentUuid,
      prompt_template: data.promptTemplate,
      input_placeholder: data.inputPlaceholder,
      config: data.config,
      display_order: data.displayOrder,
      is_enabled: data.isEnabled,
    }),
  deleteTaskTemplate: (teamId: string, templateUuid: string) =>
    api.delete(`/teams/${teamId}/task-templates/${templateUuid}`),
};
